import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import AddButton from "../../common/components/functional/addButton/AddButton";
import { FilterSearchField } from "../../common/components/functional/FilterSearchField/FilterSearchField";
import OverlaySpinner from "../../common/components/functional/overlaySpinner/OverlaySpinner";
import PageTitle from "../../common/components/functional/page/PageTitle";
import { CUSTOMERS_PAGE_SIZE } from "../../common/components/functional/pagination/constants";
import { TablePaginationProps } from "../../common/components/functional/pagination/types";
import { StripedTable } from "../../common/components/functional/stripedTable/StripedTable";
import { Nullable } from "../../common/types/nullable";
import { useTablePagination } from "../../hooks/useTablePagination";
import { canExpertAddCustomerCompany } from "../user/accessRightChecks";
import { CreateCompanyDialog } from "./create/company/CreateCompanyDialog";
import {
  fetchCustomerListPage,
  selectAllCustomerOrganizationsList,
} from "./customerOrganizationListSlice";
import CustomerOrgRow from "./customerTable/CustomerOrgRow";

const pageSize = parseInt(CUSTOMERS_PAGE_SIZE);

export const CustomersPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const customers = useAppSelector(selectAllCustomerOrganizationsList);
  const { cursor, hits, status } = useAppSelector(
    (state) => state.customerOrganizationList,
  );
  const isLoading = status === "pending";
  const error = status === "failed";

  const selectPrevTerm = (term: string) => {
    setSearchTerm(term);
  };

  const onInputChange = (term: string) => {
    setSearchTerm(term);
  };

  const dispatchSearch = useCallback(
    (cursor?: Nullable<string>) => {
      dispatch(
        fetchCustomerListPage({
          search: searchTerm || undefined,
          cursor: cursor || undefined,
          pageSize,
        }),
      );
    },
    [dispatch, searchTerm],
  );

  const { pageData, count, onPageChange, pageIndex, resetPagination } =
    useTablePagination({
      data: customers,
      cursor,
      hits,
      pageSize,
      fetchNext: (cursor) => dispatchSearch(cursor),
      preventDispatch: true,
    });

  useEffect(() => {
    dispatchSearch();
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, dispatchSearch, resetPagination]);

  const customerRows = useMemo(() => {
    return pageData.map((organization) => (
      <CustomerOrgRow key={organization.id} organization={organization} />
    ));
  }, [pageData]);

  const showNoResults = !error && !isLoading && customers.length === 0;

  const openDialog = () => {
    setIsCreateDialogOpen(true);
  };

  const closeDialog = () => {
    setIsCreateDialogOpen(false);
  };

  const paginationProps: TablePaginationProps = {
    count,
    onPageChange,
    pageIndex,
    pageSize,
  };

  return (
    <Box data-testid="customers-page-root">
      <OverlaySpinner isLoading={isLoading} />
      <PageTitle title={t("common:customers")} />
      <Box display="flex" alignItems="center">
        <FilterSearchField
          label={t("customers:searchCustomersLabel")}
          value={searchTerm}
          onChange={onInputChange}
          searchHistory={true}
          selectFromHistory={selectPrevTerm}
          localStorageName="searchCustomers"
          filters={[]}
          updateFilters={() => {}}
          translation=""
          children={<></>}
          isOpen={false}
          toggleMenu={() => {}}
          showFilterComponents={false}
          inputTestId="customers-search-input"
        />
        <AddButton
          label={t("customers:newCustomer")}
          accessRightChecks={[canExpertAddCustomerCompany]}
          variant="contained"
          onClick={openDialog}
          sx={{ marginLeft: "16px", marginTop: "-20px" }}
        />
      </Box>
      {showNoResults && (
        <Typography data-testid="customers-no-results">
          {t("common:noSearchResults")}
        </Typography>
      )}
      {!error && !showNoResults && (
        <StripedTable
          pagination={paginationProps}
          columnHeaders={[
            t("customers:customerName"),
            t("customers:businessId"),
            t("customers:salesPermission"),
          ]}
          itemRows={customerRows}
        />
      )}
      <CreateCompanyDialog open={isCreateDialogOpen} onClose={closeDialog} />
    </Box>
  );
};
